import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { useDebouncedValue } from "@msys/ui";
import { values } from "lodash-es";
import React, { useMemo, useState } from "react";
import { useLatest, useUpdateEffect } from "react-use";
import { useAuthenticatedViewer } from "../../auth/useUserData.js";
import { useLatestDefinedValue } from "../../commons/hooks/useLatestDefinedValue.js";
import { AddressDetails__AddressFragment } from "../addresses/Addresses.generated.js";
import {
  PlanningMapProjectFragment,
  PlanningMapResourceFragment,
  usePlanningMapQuery,
} from "./PlanningMap.generated.js";

// Hackescher Markt
const defaultCenter = { lat: 52.523502, lng: 13.401785 };
const defaultBounds = {
  northeast: { lat: defaultCenter.lat + 0.1, lng: defaultCenter.lng + 0.1 },
  southwest: { lat: defaultCenter.lat - 0.1, lng: defaultCenter.lng - 0.1 },
};

const LIMIT_USERS_ON_MAP = 50;

export type PlanningMapBuilding = PlanningMapProjectFragment["buildingInfo"] & {
  projects: PlanningMapProjectFragment[];
};
export type PlanningMapResource = {
  address: AddressDetails__AddressFragment & {
    location: NonNullable<AddressDetails__AddressFragment["location"]>;
  };
  craftsmen: PlanningMapResourceFragment[];
};

interface LatLng {
  lat: number;
  lng: number;
}

export interface PlanningMapBoundsBox {
  northeast: LatLng;
  southwest: LatLng;
}

interface UsePlanningMapDataOptions {
  projects: PlanningMapProjectFragment[];
  initialZoom: number;
  initialCenter?: LatLng;
  initialSelectedBuildingId?: string;
  initialSelectedResourceId?: string;
}

export const usePlanningMapData = ({
  projects,
  initialZoom,
  initialCenter,
  initialSelectedBuildingId,
  initialSelectedResourceId,
}: UsePlanningMapDataOptions) => {
  const viewer = useAuthenticatedViewer();
  const organisationAddress =
    viewer.organisation.branchAddress ?? viewer.organisation.billingAddress;

  const [center, setCenter] = useState<LatLng>(
    initialCenter ??
      (organisationAddress?.location
        ? {
            lat: organisationAddress.location.lat,
            lng: organisationAddress.location.lng,
          }
        : defaultCenter)
  );
  const [zoom, setZoom] = useState<number>(initialZoom);

  const [lastCoords, setLastCoords] = useState<{
    center: LatLng | null;
    bounds: PlanningMapBoundsBox | null;
  }>({ center: null, bounds: null });
  const lastCoordsLatest = useLatest(lastCoords);
  const lastCoordsDebounced = useDebouncedValue(lastCoords, 1000, {
    leading: false,
    trailing: true,
  });

  const client = useApolloClient();
  const query = usePlanningMapQuery({
    client,
    variables: {
      center: lastCoordsDebounced.center || defaultCenter,
      bounds: lastCoordsDebounced.bounds || defaultBounds,
      limit: LIMIT_USERS_ON_MAP + 1,
    },
    skip: !lastCoordsDebounced.center || !lastCoordsDebounced.bounds,
    fetchPolicy: "cache-and-network",
  });

  const resources = useLatestDefinedValue(
    React.useMemo(
      () =>
        getDataOrNull(query.data?.organisationMemberships)?.edges.map(
          edge => edge.node
        ),
      [query.data?.organisationMemberships]
    )
  );

  useUpdateEffect(() => {
    const newCenter =
      initialCenter ??
      (organisationAddress?.location
        ? {
            lat: organisationAddress.location.lat,
            lng: organisationAddress.location.lng,
          }
        : undefined);
    if (newCenter) setCenter(newCenter);
  }, [initialCenter, organisationAddress]);

  useUpdateEffect(() => {
    // Assign unconditionally: `initialZoom` is a required number, so a
    // truthiness check would silently drop the valid zoom level 0 and leave
    // the previous zoom active.
    setZoom(initialZoom);
  }, [initialZoom]);

  const [selectedBuildingId, setSelectedBuildingId] = useState<
    string | undefined
  >(initialSelectedBuildingId);

  const [selectedResourceId, setSelectedResourceId] = useState<
    string | undefined
  >(initialSelectedResourceId);

  const mapBuildings: PlanningMapBuilding[] = useMemo(() => {
    const buildings: Record<string, PlanningMapBuilding> = {};

    projects.forEach(project => {
      if (
        !project?.buildingInfo?.id ||
        // we only need buildings with location here!
        !project?.buildingInfo.buildingAddress?.location
      )
        return;

      if (!buildings[project.buildingInfo.id]) {
        buildings[project.buildingInfo.id] = {
          ...project.buildingInfo,
          projects: [],
        };
      }

      buildings[project.buildingInfo.id].projects.push(project);
    });

    return values(buildings) as PlanningMapBuilding[];
  }, [projects]);

  const mapResources: PlanningMapResource[] = useMemo(() => {
    const craftsmen: Record<string, PlanningMapResource> = {};
    // The query fetches LIMIT + 1 users as an overflow sentinel (see
    // `isOverLimit`); only the first LIMIT are displayed.
    (resources ?? [])
      .slice(0, LIMIT_USERS_ON_MAP)
      .flatMap(r =>
        r.homeAddress && r.homeAddress.location
          ? [
              {
                resource: r,
                address: r.homeAddress,
                location: r.homeAddress.location,
              },
            ]
          : []
      )
      .forEach(resource => {
        const key = `${resource.location.lat}-${resource.location.lng}`;
        craftsmen[key] = craftsmen[key] || {
          address: resource.address,
          craftsmen: [],
        };
        craftsmen[key].craftsmen.push(resource.resource);
      });

    return values(craftsmen) as PlanningMapResource[];
  }, [resources]);

  const selectedBuilding: PlanningMapBuilding | undefined = useMemo(
    () =>
      selectedBuildingId
        ? mapBuildings.find(b => b.id === selectedBuildingId)
        : undefined,
    [mapBuildings, selectedBuildingId]
  );

  const selectedResource: PlanningMapResource | undefined = useMemo(
    () =>
      selectedResourceId
        ? mapResources.find(r =>
            r.craftsmen.some(c => c.id === selectedResourceId)
          )
        : undefined,
    [mapResources, selectedResourceId]
  );

  const setLastCoordsIfChanged = React.useCallback(
    (newCenter: LatLng, newBounds: PlanningMapBoundsBox) => {
      if (
        newCenter.lat !== lastCoordsLatest.current.center?.lat ||
        newCenter.lng !== lastCoordsLatest.current.center?.lng ||
        newBounds.northeast.lat !==
          lastCoordsLatest.current.bounds?.northeast.lat ||
        newBounds.northeast.lng !==
          lastCoordsLatest.current.bounds?.northeast.lng ||
        newBounds.southwest.lat !==
          lastCoordsLatest.current.bounds?.southwest.lat ||
        newBounds.southwest.lng !==
          lastCoordsLatest.current.bounds?.southwest.lng
      ) {
        setLastCoords({ center: newCenter, bounds: newBounds });
      }
    },
    [lastCoordsLatest]
  );

  return {
    organisationAddress,
    center,
    zoom,
    setLastCoordsIfChanged,
    selectedBuildingId,
    setSelectedBuildingId,
    selectedResourceId,
    setSelectedResourceId,
    mapBuildings,
    mapResources,
    selectedBuilding,
    selectedResource,
    // Based on the ungrouped user count: the query fetches LIMIT + 1 users,
    // so this must count what the query counts. `mapResources` groups users
    // sharing an address — its length can be far below the limit even when
    // the query result was truncated.
    isOverLimit: (resources ?? []).length > LIMIT_USERS_ON_MAP,
  };
};

export { LIMIT_USERS_ON_MAP };
